<p-dialog
    [header]="null"
    [visible]="(viewModel$ | async)?.open"
    [modal]="true"
    [draggable]="false"
    [dismissableMask]="!(viewModel$ | async)?.pushLive"
    [closable]="!(viewModel$ | async)?.pushLive"
    [closeOnEscape]="!(viewModel$ | async)?.pushLive"
    [style]="{ width: '46rem', maxWidth: '95vw' }"
    (visibleChange)="!$event && store.dispatch(actions.dialogClosed())"
>
    <!-- Requirement: from the moment step one is answered, what the money is
         against and what it comes to are on screen everywhere — an operator
         must never be in doubt whether they are collecting a deposit or a
         payment on a named invoice. -->
    <ng-template pTemplate="header">
        <div class="create-transaction-header">
            <span class="create-transaction-dialog-title">Take transaction</span>

            <div
                class="create-transaction-collecting"
                *ngIf="(viewModel$ | async)?.collecting as collecting"
            >
                <ng-container
                    *ngTemplateOutlet="collectingValue; context: { $implicit: collecting.target }"
                ></ng-container>
                <span class="create-transaction-collecting-divider" aria-hidden="true">·</span>
                <ng-container
                    *ngTemplateOutlet="collectingValue; context: { $implicit: collecting.amount }"
                ></ng-container>
                <ng-container *ngIf="(viewModel$ | async)?.cardPrice as card">
                    <span class="create-transaction-collecting-divider" aria-hidden="true">·</span>
                    <span class="create-transaction-collecting-value">
                        On card {{ card.total | currency: (viewModel$ | async)?.currency }}
                    </span>
                </ng-container>
            </div>
        </div>
    </ng-template>

    <ng-template #collectingValue let-value>
        <button
            *ngIf="value.step as step; else inertValue"
            type="button"
            class="create-transaction-collecting-value"
            [attr.aria-label]="value.action"
            (click)="store.dispatch(actions.collectingValueClicked({ step }))"
        >
            <i class="pi pi-pencil" aria-hidden="true"></i>
            {{ value.text }}
        </button>
        <ng-template #inertValue>
            <span class="create-transaction-collecting-value">
                {{ value.text }}
            </span>
        </ng-template>
    </ng-template>

    <ng-container *ngIf="viewModel$ | async as viewModel">

        <app-bluesage-terminal-push *ngIf="viewModel.pushInFlight"></app-bluesage-terminal-push>

        <div class="create-transaction" *ngIf="!viewModel.pushInFlight">

            <div class="create-transaction-steps" *ngIf="viewModel.stepNumber > 0">
                <span
                    *ngFor="let step of steps; index as i"
                    class="create-transaction-step"
                    [class.filled]="i < viewModel.stepNumber"
                ></span>
            </div>

            <!-- Requirement: this screen completes and never releases —
                 releasing is the opposite money action and belongs on the
                 transaction row's own ⋮ menu, where it names the hold it acts
                 on. -->
            <ng-container *ngIf="viewModel.step === 'holds'">
                <h3 class="create-transaction-title" *ngIf="viewModel.holdTiles.length">
                    {{ viewModel.holdsPrompt }}
                </h3>

                <div class="create-transaction-methods" *ngIf="viewModel.holdTiles.length">
                    <button
                        type="button"
                        class="create-transaction-method"
                        *ngFor="let tile of viewModel.holdTiles"
                        [disabled]="viewModel.busy"
                        (click)="store.dispatch(actions.holdTileClicked({
                            transactionId: tile.transactionId,
                            amountMinor: tile.amountMinor
                        }))"
                    >
                        <i class="pi pi-lock"></i>
                        <span class="create-transaction-method-body">
                            <span class="create-transaction-method-label">{{ tile.label }}</span>
                        </span>
                        <span class="create-transaction-method-amount">
                            {{ tile.amount | currency: viewModel.currency }}
                        </span>
                    </button>
                </div>

                <p class="p-text-secondary create-transaction-hint"
                   *ngIf="!viewModel.holdTiles.length">
                    This job is no longer holding any authorizations.
                </p>
            </ng-container>

            <ng-container *ngIf="viewModel.step === 'targets'">
                <h3 class="create-transaction-title">What are you collecting?</h3>

                <p-messages
                    severity="error"
                    *ngIf="viewModel.jobLoadFailed"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            This job could not be read in the zone you are in, so
                            there is nothing to collect against. Open the job to
                            take a payment on it.
                        </span>
                    </ng-template>
                </p-messages>

                <div
                    class="create-transaction-methods"
                    role="status"
                    aria-label="Reading this job"
                    *ngIf="viewModel.jobLoading"
                >
                    <div
                        class="create-transaction-method create-transaction-method-loading"
                        *ngFor="let placeholder of loadingTargetTiles"
                    >
                        <p-skeleton shape="circle" size="1.25rem"></p-skeleton>
                        <span class="create-transaction-method-body">
                            <span class="create-transaction-method-label">
                                <p-skeleton width="60%" height="0.9rem"></p-skeleton>
                            </span>
                        </span>
                        <p-skeleton width="3.5rem" height="0.9rem"></p-skeleton>
                    </div>
                </div>

                <div
                    class="create-transaction-methods"
                    role="radiogroup"
                    *ngIf="!viewModel.jobLoading && !viewModel.jobLoadFailed"
                >
                    <button
                        type="button"
                        role="radio"
                        class="create-transaction-method"
                        *ngFor="let tile of viewModel.targetTiles"
                        [class.selected]="tile.selected"
                        [attr.aria-checked]="tile.selected"
                        (click)="store.dispatch(tile.action)"
                    >
                        <i [class]="tile.icon"></i>
                        <span class="create-transaction-method-body">
                            <span class="create-transaction-method-label">{{ tile.label }}</span>
                            <small class="p-text-secondary" *ngIf="tile.dueDate">
                                Due {{ tile.dueDate | date: 'mediumDate' }}
                            </small>
                        </span>
                        <span class="create-transaction-method-amount">
                            {{ tile.amount | currency: viewModel.currency }}
                        </span>
                    </button>
                </div>

                <p class="p-text-secondary create-transaction-hint"
                   *ngIf="viewModel.invoiceEmptyHint as hint">
                    {{ hint }}
                </p>
            </ng-container>

            <ng-container *ngIf="viewModel.step === 'events'">
                <h3 class="create-transaction-title">What goes on the invoice?</h3>

                <div class="create-transaction-methods" role="group">
                    <div
                        class="create-transaction-method"
                        *ngFor="let tile of viewModel.invoiceEventTiles"
                        (click)="store.dispatch(tile.action)"
                    >
                        <p-radioButton
                            *ngIf="viewModel.invoiceEventsSingleSelect; else eventCheckbox"
                            name="create-transaction-invoice-event"
                            [value]="tile.eventId"
                            [ngModel]="tile.selected ? tile.eventId : null"
                            [ariaLabel]="tile.label"
                            (click)="$event.stopPropagation()"
                            (onClick)="store.dispatch(tile.action)"
                        ></p-radioButton>
                        <ng-template #eventCheckbox>
                            <p-checkbox
                                [binary]="true"
                                [ngModel]="tile.selected"
                                [ariaLabel]="tile.label"
                                (click)="$event.stopPropagation()"
                                (onChange)="store.dispatch(tile.action)"
                            ></p-checkbox>
                        </ng-template>
                        <span class="create-transaction-method-body">
                            <span class="create-transaction-method-label">{{ tile.label }}</span>
                            <small class="p-text-secondary" *ngIf="tile.start">
                                {{ tile.start * 1000 | date: 'mediumDate' }}
                            </small>
                        </span>
                        <span class="create-transaction-method-amount">
                            {{ tile.amount | currency: viewModel.currency }}
                        </span>
                    </div>
                </div>
            </ng-container>

            <ng-container *ngIf="viewModel.step === 'amount'">
                <h3 class="create-transaction-title">How much are they paying?</h3>

                <p-inputNumber
                    appSelectOnFocus
                    styleClass="create-transaction-amount"
                    inputId="create-transaction-amount"
                    mode="currency"
                    [currency]="viewModel.currency"
                    [min]="0"
                    [ngModel]="viewModel.amount"
                    (ngModelChange)="store.dispatch(
                        actions.amountChanged({ amountMinor: toMinor($event) })
                    )"
                ></p-inputNumber>

                <p class="p-error create-transaction-hint" *ngIf="viewModel.amountOverBalance">
                    This is more than the invoice's balance of
                    {{ viewModel.selectedTotal | currency: viewModel.currency }}.
                </p>

                <div class="create-transaction-chips">
                    <p-button
                        *ngIf="viewModel.selectedTotalLabel as chipLabel"
                        [label]="chipLabel + ' ' + (viewModel.selectedTotal | currency: viewModel.currency)"
                        size="small"
                        [rounded]="true"
                        [text]="true"
                        (onClick)="store.dispatch(actions.amountChipClicked({
                            amountMinor: viewModel.selectedTotalMinor
                        }))"
                    ></p-button>
                </div>
            </ng-container>

            <ng-container *ngIf="viewModel.step === 'method'">
                <h3 class="create-transaction-title">How are they paying?</h3>

                <p class="p-error create-transaction-hint" *ngIf="viewModel.paymentTypesFailed">
                    Could not load this zone's payment types, so some methods are
                    missing here. Close this and try again.
                </p>

                <div class="create-transaction-methods">
                    <button
                        type="button"
                        class="create-transaction-method"
                        *ngFor="let tile of viewModel.methods"
                        [class.selected]="tile.selected"
                        (click)="store.dispatch(actions.methodTileClicked({ method: tile.method }))"
                    >
                        <i [class]="tile.icon"></i>
                        <span class="create-transaction-method-label">{{ tile.label }}</span>
                    </button>
                    <button
                        type="button"
                        class="create-transaction-method"
                        *ngFor="let tile of viewModel.paymentTypeTiles"
                        [class.selected]="tile.selected"
                        (click)="store.dispatch(actions.paymentTypeTileClicked({
                            paymentTypeId: tile.paymentTypeId
                        }))"
                    >
                        <i class="pi pi-wallet"></i>
                        <span class="create-transaction-method-label">{{ tile.label }}</span>
                    </button>
                </div>
            </ng-container>

            <ng-container *ngIf="viewModel.step === 'details'">
                <h3 class="create-transaction-title">{{ viewModel.methodPrompt }}</h3>

                <ng-container *ngIf="viewModel.method === 'terminal' || viewModel.method === 'preauth'">
                    <app-bluesage-terminal-picker></app-bluesage-terminal-picker>
                </ng-container>

                <ng-container *ngIf="viewModel.method === 'card'">

                    <!-- Requirement: no PAN may reach this application —
                         BlueSage's own form renders the card fields here, this
                         component never draws card input. -->
                    <app-bluesage-card-fields
                        *ngIf="viewModel.embedUrl"
                        [embedUrl]="viewModel.embedUrl"
                        (cardTokenReceived)="store.dispatch(actions.cardTokenReceived({
                            cardToken: $event.cardToken,
                            last4: $event.last4
                        }))"
                    ></app-bluesage-card-fields>

                    <ng-container *ngIf="!viewModel.embedUrl">

                        <div class="create-transaction-row"
                             *ngFor="let card of viewModel.cardsOnFile">
                            <button
                                type="button"
                                role="radio"
                                class="create-transaction-option"
                                [class.selected]="card.token === viewModel.selectedOnFileToken"
                                [attr.aria-checked]="card.token === viewModel.selectedOnFileToken"
                                [disabled]="viewModel.busy"
                                (click)="store.dispatch(
                                    actions.cardOnFileSelected({ token: card.token })
                                )"
                            >
                                <i class="pi pi-credit-card"></i>
                                <span class="create-transaction-option-body">
                                    <span class="create-transaction-option-title">
                                        {{ card.cardBrand || 'Card' }} ••••{{ card.last4 || '' }}
                                    </span>
                                    <!-- Requirement: `BlueSagePaymentProfile`
                                         carries no expiry date, so this shows
                                         when the card was saved instead of
                                         inventing one. -->
                                    <small class="p-text-secondary" *ngIf="card.createdAt">
                                        Saved {{ card.createdAt | date: 'mediumDate' }}
                                    </small>
                                </span>
                            </button>
                            <p-button
                                icon="pi pi-trash"
                                severity="danger"
                                [text]="true"
                                size="small"
                                [disabled]="viewModel.busy"
                                pTooltip="Remove this card"
                                (onClick)="store.dispatch(
                                    actions.deleteCardOnFileClicked({ token: card.token })
                                )"
                            ></p-button>
                        </div>

                        <div class="create-transaction-row" *ngIf="viewModel.newCard as newCard">
                            <button
                                type="button"
                                role="radio"
                                class="create-transaction-option"
                                [class.selected]="viewModel.newCardSelected"
                                [attr.aria-checked]="viewModel.newCardSelected"
                                [disabled]="viewModel.busy"
                                (click)="store.dispatch(actions.newCardSelected())"
                            >
                                <i class="pi pi-credit-card"></i>
                                <span class="create-transaction-option-body">
                                    <span class="create-transaction-option-title">
                                        {{ newCard.label }}
                                    </span>
                                    <small class="p-text-secondary">
                                        {{ viewModel.saveCard && viewModel.newCardSelected
                                            ? 'Saved when you charge it'
                                            : 'Not saved' }}
                                    </small>
                                </span>
                            </button>
                            <p-button
                                icon="pi pi-trash"
                                severity="danger"
                                [text]="true"
                                size="small"
                                [disabled]="viewModel.busy"
                                pTooltip="Discard this card — it is not saved anywhere"
                                (onClick)="store.dispatch(actions.discardNewCardClicked())"
                            ></p-button>
                        </div>

                        <!-- Requirement: vaulting rides on the charge, and the
                             charge request only carries `vault` alongside a
                             one-time token — so this toggle appears only for
                             the selected new card; a ticked box beside a saved
                             card being charged would promise something the
                             request cannot fulfill. -->
                        <div class="create-transaction-toggle" *ngIf="viewModel.newCardSelected">
                            <p-checkbox
                                inputId="create-transaction-save-card"
                                [binary]="true"
                                [ngModel]="viewModel.saveCard"
                                (ngModelChange)="store.dispatch(
                                    actions.saveCardToggled({ saveCard: $event })
                                )"
                            ></p-checkbox>
                            <label for="create-transaction-save-card">
                                Save this card for next time
                            </label>
                        </div>

                        <button
                            type="button"
                            class="create-transaction-option create-transaction-add"
                            [disabled]="viewModel.busy"
                            (click)="store.dispatch(actions.keyInCardButtonClicked())"
                        >
                            <i
                                class="pi"
                                [ngClass]="viewModel.openingCardForm
                                    ? 'pi-spin pi-spinner'
                                    : 'pi-plus'"
                            ></i>
                            <span class="create-transaction-option-title">
                                {{ viewModel.newCard
                                    ? 'Enter a different card'
                                    : 'Enter new card' }}
                            </span>
                        </button>
                    </ng-container>
                </ng-container>

                <ng-container *ngIf="viewModel.method === 'payLink'">
                    <p-messages
                        severity="warn"
                        *ngIf="viewModel.customerUnreachable"
                        [closable]="false"
                    >
                        <ng-template pTemplate>
                            <span *ngIf="!viewModel.payUrl">
                                <strong>No phone or email on the customer record.</strong>
                                The link will be written to the job as a comment for you to
                                send by hand.
                            </span>
                            <span *ngIf="viewModel.payUrl">
                                <strong>No phone or email on the customer record.</strong>
                                The link was written to the job as a comment — send it to the
                                customer by hand. The payment still appears on the job
                                automatically once they pay it.
                            </span>
                        </ng-template>
                    </p-messages>

                    <div class="create-transaction-toggle">
                        <p-checkbox
                            inputId="create-transaction-vault-paylink"
                            [binary]="true"
                            [ngModel]="viewModel.vaultOnPayLink"
                            (ngModelChange)="store.dispatch(
                                actions.vaultOnPayLinkToggled({ vaultOnPayLink: $event })
                            )"
                        ></p-checkbox>
                        <label for="create-transaction-vault-paylink">
                            Keep the card on file when they pay the link
                        </label>
                    </div>

                    <!-- Requirement: the URL itself must be shown, not just
                         confirmation it was sent — an operator on the phone
                         with the customer needs to read it aloud. -->
                    <ng-container *ngIf="viewModel.payUrl">
                        <p-messages
                            severity="success"
                            *ngIf="!viewModel.customerUnreachable"
                            [closable]="false"
                        >
                            <ng-template pTemplate>
                                <span>
                                    Pay link sent to the customer. The payment appears on
                                    the job automatically when they pay it — nothing else
                                    to do here.
                                </span>
                            </ng-template>
                        </p-messages>

                        <div class="create-transaction-paylink">
                            <span>{{ viewModel.payUrl }}</span>
                            <p-button
                                icon="pi pi-copy"
                                severity="secondary"
                                size="small"
                                [text]="true"
                                pTooltip="Copy the link"
                                (onClick)="store.dispatch(
                                    actions.payLinkCopyButtonClicked({ payUrl: viewModel.payUrl })
                                )"
                            ></p-button>
                        </div>
                    </ng-container>
                </ng-container>

                <ng-container *ngIf="viewModel.method === 'squareCheckout'">
                    <p-messages
                        severity="warn"
                        *ngIf="viewModel.customerUnreachable"
                        [closable]="false"
                    >
                        <ng-template pTemplate>
                            <span>
                                <strong>No phone or email on the customer record.</strong>
                                The link will be written to the transaction's remarks for
                                you to send by hand.
                            </span>
                        </ng-template>
                    </p-messages>

                    <p class="create-transaction-hint" *ngIf="!viewModel.customerUnreachable">
                        Sends a Square Checkout link to the customer by text and email.
                    </p>
                </ng-container>

                <ng-container *ngIf="viewModel.method === 'recordCard'">
                    <p class="p-error create-transaction-hint" *ngIf="viewModel.paymentTypesFailed">
                        Could not load this zone's payment types, so some methods are
                        missing here. Close this and try again.
                    </p>

                    <div class="create-transaction-methods">
                        <button
                            type="button"
                            class="create-transaction-method"
                            *ngFor="let tile of viewModel.cardMethods"
                            [class.selected]="tile.selected"
                            (click)="store.dispatch(actions.methodTileClicked({ method: tile.method }))"
                        >
                            <i [class]="tile.icon"></i>
                            <span class="create-transaction-method-label">{{ tile.label }}</span>
                        </button>
                    </div>

                    <p
                        class="p-error create-transaction-hint"
                        *ngIf="viewModel.paymentTypesLoaded
                            && !viewModel.paymentTypesFailed
                            && !viewModel.cardMethods.length"
                    >
                        This zone has no card payment type set up to record against.
                    </p>
                </ng-container>

                <ng-container *ngIf="viewModel.isRecordingManualPayment">

                    <p class="p-error create-transaction-hint" *ngIf="viewModel.paymentTypesFailed">
                        Could not load the manual payment types — this is a connection
                        problem, not a setup one. Close this and try again.
                    </p>
                </ng-container>

                <ng-container
                    *ngIf="viewModel.isRecordingManualPayment || viewModel.method === 'squareCheckout'"
                >
                    <quill-editor
                        format="object"
                        placeholder="Enter any additional remarks (optional)"
                        [modules]="remarksModules"
                        [styles]="{ height: '120px' }"
                        [ngModel]="viewModel.remarks"
                        (ngModelChange)="store.dispatch(
                            actions.remarksChanged({ remarks: $event })
                        )"
                    ></quill-editor>
                </ng-container>

                <div
                    class="create-transaction-toggle create-transaction-spaced"
                    *ngIf="viewModel.receiptChoice === 'toggle'"
                >
                    <p-checkbox
                        inputId="create-transaction-send-receipt"
                        [binary]="true"
                        [ngModel]="viewModel.sendReceipt"
                        (ngModelChange)="store.dispatch(
                            actions.sendReceiptToggled({ sendReceipt: $event })
                        )"
                    ></p-checkbox>
                    <label for="create-transaction-send-receipt">
                        Send a receipt to the customer
                    </label>
                </div>

                <p-messages
                    severity="warn"
                    *ngIf="viewModel.receiptChoice === 'unavailable'"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            <strong>No phone or email on the customer record.</strong>
                            No receipt can be sent for this payment.
                        </span>
                    </ng-template>
                </p-messages>
            </ng-container>

            <ng-container *ngIf="viewModel.step === 'complete'">
                <h3 class="create-transaction-title">
                    How much do you want to complete the authorization for?
                </h3>

                <!-- Requirement: a completion amount may exceed what was
                     authorized — the terminal allows this — so the amount
                     stays editable here rather than fixed to the held amount. -->
                <p-inputNumber
                    appSelectOnFocus
                    styleClass="create-transaction-amount"
                    inputId="create-transaction-complete-amount"
                    mode="currency"
                    [currency]="viewModel.currency"
                    [min]="0"
                    [max]="viewModel.balance"
                    [ngModel]="viewModel.amount"
                    (ngModelChange)="store.dispatch(
                        actions.completeAmountChanged({ amountMinor: toMinor($event) })
                    )"
                ></p-inputNumber>

                <p class="create-transaction-hint" *ngIf="viewModel.cardPrice as card">
                    On a card the customer pays <strong>{{ card.total | currency: viewModel.currency }}</strong>
                </p>

                <p-messages
                    severity="warn"
                    *ngIf="viewModel.balanceExceedsHold"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            The balance due of
                            <strong>{{ viewModel.balance | currency: viewModel.currency }}</strong>
                            is more than the
                            <strong>{{ viewModel.heldAmount | currency: viewModel.currency }}</strong>
                            held.
                            <ng-container *ngIf="viewModel.completeShortfall > 0">
                                <strong>{{ viewModel.completeShortfall | currency: viewModel.currency }}</strong>
                                will still be owing after this.
                            </ng-container>
                        </span>
                    </ng-template>
                </p-messages>

                <p-messages
                    severity="error"
                    *ngIf="viewModel.completeAmountExceedsBalance || viewModel.nothingOwing"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span *ngIf="viewModel.nothingOwing">
                            Nothing is owing on this job — release the hold instead.
                        </span>
                        <span *ngIf="!viewModel.nothingOwing">
                            That is more than the job's balance of
                            <strong>{{ viewModel.balance | currency: viewModel.currency }}</strong>.
                            Complete for the balance or less.
                        </span>
                    </ng-template>
                </p-messages>

                <p-messages
                    severity="warn"
                    *ngIf="viewModel.completeAmountExceedsHold && !viewModel.completeAmountExceedsBalance"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            That is more than the
                            <strong>{{ viewModel.heldAmount | currency: viewModel.currency }}</strong>
                            authorized. The card issuer may decline the difference — consider
                            completing for the hold and taking the rest as a separate payment.
                        </span>
                    </ng-template>
                </p-messages>

                <p-messages
                    severity="warn"
                    *ngIf="!viewModel.openPreauthTransactionId"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            This authorization is no longer open — it has been completed or
                            released. Close this and check the job's transactions.
                        </span>
                    </ng-template>
                </p-messages>

                <!-- Requirement: the tip prompt gives the customer about 30
                     seconds and is not settable — the customer must be at the
                     terminal before this is sent, so the confirmation is
                     explicit rather than assumed. -->
                <p-messages
                    severity="warn"
                    *ngIf="viewModel.openPreauthTransactionId"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            Holding <strong>{{ viewModel.heldAmount | currency: viewModel.currency }}</strong> on
                            terminal
                            <strong>{{ viewModel.holdDevice || viewModel.device }}</strong>.
                            The customer has about <strong>30 seconds</strong> to answer the
                            tip prompt once this is sent — confirm they are at the terminal now.
                        </span>
                    </ng-template>
                </p-messages>

                <p-messages
                    severity="error"
                    *ngIf="viewModel.completionDeviceOffline"
                    [closable]="false"
                >
                    <ng-template pTemplate>
                        <span>
                            Terminal {{ viewModel.holdDevice || viewModel.device }} is off. Turn
                            it on before completing — this authorization can only be completed
                            on the terminal it was taken on.
                        </span>
                    </ng-template>
                </p-messages>
            </ng-container>

            <p-messages severity="error" *ngIf="viewModel.error" [closable]="false">
                <ng-template pTemplate><span>{{ viewModel.error }}</span></ng-template>
            </p-messages>
        </div>
    </ng-container>

    <ng-template pTemplate="footer">
        <ng-container *ngIf="viewModel$ | async as viewModel">
            <div class="create-transaction-footer" *ngIf="!viewModel.pushInFlight">

                <p-button
                    *ngIf="!viewModel.embedUrl
                        && !(viewModel.method === 'payLink' && viewModel.payUrl)
                        && (viewModel.step === 'events'
                            || viewModel.step === 'amount'
                            || viewModel.step === 'method'
                            || viewModel.step === 'details')"
                    label="Back"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    [text]="true"
                    [disabled]="viewModel.busy"
                    (onClick)="store.dispatch(actions.backButtonClicked())"
                ></p-button>

                <p-button
                    *ngIf="viewModel.embedUrl"
                    label="Back"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    [text]="true"
                    [disabled]="viewModel.busy"
                    (onClick)="store.dispatch(actions.cardFormClosed())"
                ></p-button>

                <p-button
                    *ngIf="viewModel.step === 'targets' || viewModel.step === 'holds'"
                    label="Close"
                    severity="secondary"
                    [text]="true"
                    (onClick)="store.dispatch(actions.dialogClosed())"
                ></p-button>

                <p-button
                    *ngIf="viewModel.step === 'complete'"
                    label="Cancel"
                    severity="secondary"
                    [text]="true"
                    (onClick)="store.dispatch(actions.completeCancelled())"
                ></p-button>

                <span class="create-transaction-footer-spacer"></span>

                <p-button
                    *ngIf="viewModel.step === 'holds'"
                    label="Create new transaction"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="secondary"
                    [outlined]="true"
                    [disabled]="viewModel.busy"
                    (onClick)="store.dispatch(actions.createNewTransactionButtonClicked())"
                ></p-button>

                <p-button
                    *ngIf="viewModel.step === 'events'"
                    label="Continue"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    [disabled]="!viewModel.canContinueToAmount"
                    (onClick)="store.dispatch(actions.continueToAmountButtonClicked())"
                ></p-button>

                <p-button
                    *ngIf="viewModel.step === 'amount'"
                    label="Choose payment method"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    [disabled]="viewModel.amountMinor <= 0 || viewModel.amountOverBalance"
                    (onClick)="store.dispatch(actions.continueToMethodButtonClicked())"
                ></p-button>

                <ng-container *ngIf="viewModel.step === 'details'">
                    <p-button
                        *ngIf="viewModel.method === 'preauth'"
                        [label]="'Send auth ' + (viewModel.amount | currency: viewModel.currency)"
                        [loading]="viewModel.creating"
                        [disabled]="viewModel.busy || !viewModel.canSend"
                        (onClick)="store.dispatch(
                            actions.sendToTerminalButtonClicked({ intent: 'preauth' })
                        )"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.method === 'terminal'"
                        [label]="'Send sale ' + (viewModel.amount | currency: viewModel.currency)"
                        [loading]="viewModel.creating"
                        [disabled]="viewModel.busy || !viewModel.canSend"
                        (onClick)="store.dispatch(
                            actions.sendToTerminalButtonClicked({ intent: 'sale' })
                        )"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.method === 'card' && !viewModel.embedUrl"
                        [label]="'Charge ' + (viewModel.amount | currency: viewModel.currency)"
                        [loading]="viewModel.creating"
                        [disabled]="viewModel.busy || !viewModel.canCharge"
                        (onClick)="store.dispatch(actions.chargeCardButtonClicked())"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.method === 'payLink' && !viewModel.payUrl"
                        label="Send pay link"
                        [loading]="viewModel.creating"
                        [disabled]="viewModel.busy || !viewModel.canSendPayLink"
                        (onClick)="store.dispatch(actions.sendPayLinkButtonClicked())"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.method === 'payLink' && viewModel.payUrl"
                        label="Send a new link"
                        severity="secondary"
                        [text]="true"
                        [disabled]="viewModel.busy || !viewModel.canSendPayLink"
                        (onClick)="store.dispatch(actions.sendNewPayLinkButtonClicked())"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.method === 'payLink' && viewModel.payUrl"
                        label="Done"
                        [disabled]="viewModel.busy"
                        (onClick)="store.dispatch(actions.doneButtonClicked())"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.isRecordingManualPayment"
                        [label]="viewModel.recordLabel"
                        [loading]="viewModel.creating"
                        [disabled]="viewModel.busy || !viewModel.canRecord"
                        (onClick)="store.dispatch(actions.recordPaymentButtonClicked())"
                    ></p-button>

                    <p-button
                        *ngIf="viewModel.method === 'squareCheckout'"
                        label="Send Square Checkout"
                        [loading]="viewModel.creating"
                        [disabled]="viewModel.busy || !viewModel.canSendSquareCheckout"
                        (onClick)="store.dispatch(actions.sendSquareCheckoutButtonClicked())"
                    ></p-button>
                </ng-container>

                <ng-container *ngIf="viewModel.step === 'complete'">
                    <p-button
                        [label]="'Complete ' + (viewModel.amount | currency: viewModel.currency)"
                        [disabled]="!viewModel.canComplete"
                        (onClick)="store.dispatch(pushActions.completePreauthConfirmed({
                            transactionId: viewModel.openPreauthTransactionId,
                            epi: viewModel.selectedEpi,
                            amountMinor: viewModel.amountMinor
                        }))"
                    ></p-button>
                </ng-container>
            </div>
        </ng-container>
    </ng-template>
</p-dialog>
